import * as Sentry from "@sentry/react";
import { Order, Variant } from "../components/islands/OrderForm/types.ts";
import { sha256 } from "./helpers.ts";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]; // GTM
  _conv_q: Record<string, any>[]; // Convert.com
};

declare const window: WindowWithDataLayer;

const getPrices = (product: Variant, consumption: string) => {
  const pricePerKwh = product.name.includes('Pörssisähkö') ? product.energy_price + 8.82 : product.energy_price;
  const priceUse = pricePerKwh && consumption ? (pricePerKwh / 100) * parseFloat(consumption) : 0;
  const priceMonth = product.monthly_price ? product.monthly_price * 12 : 0;
  return { priceUse, priceMonth };
}

export const addToCartEvent = (product: Variant, formValues: Order) => {
  try {
    const { priceUse, priceMonth } = getPrices(product, formValues['consumption-estimate']);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'EUR',
        value: priceUse + priceMonth,
        items: [
          {
            item_id: product.contract_template_id,
            item_name: product.name,
            index: 0,
            item_brand: 'Sähkösopimus',
            item_category: formValues['apartment-type'],
            item_category2: formValues['preferences'],
            item_category3: formValues['consumption-estimate'],
            item_category4: formValues['apartment-size'],
            item_category5: 'Manually selected',
            item_list_id: 'contract',
            item_list_name: 'Sähkösopimus',
            item_variant: product.contract_period.label,
            location_id: 'ChIJjY1xqQuhmkYRHh-QHYPuuGs',
            price: priceUse + priceMonth,
            quantity: 1
          }
        ]
      }
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
};


export const beginCheckoutEvent = (formValues: Order, product: Variant) => {
  try {
    const { priceUse, priceMonth } = getPrices(product, formValues['consumption-estimate']);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        value: priceUse + priceMonth,
        currency: 'EUR',
        //coupon: "SUMMER_SALE",
        items: [
          {
            item_id: product.contract_template_id, // sopimuspohjan id? Tuulisähköllä esim. 21 ja 75 riippuen kestosta
            item_name: product.name, //"Tuulisähkö"
            //coupon: "xxx", //sitten kun kupongit tulee käyttöön
            index: 0,
            item_brand: 'Sähkösopimus', // Olisiko tämä parempi paikka laittaa tilausputken tyyppi: Sähkösopimus, OEK jne.
            item_variant: product.contract_period.label, //pituus tai paketin koko S M L XL
            item_category: formValues['apartment-type'], //asumismuoto appartment-type
            item_category2: formValues['preferences'], //Mitä toivoisit sähköltä
            item_category3: formValues['consumption-estimate'], //kulutusarvio "consumption-estimate"
            item_category4: formValues['apartment-size'], // asunnon koko "appartment-size"
            //item_category5: true, // oliko valinta algoritmin suositus vai ei
            location_id: 'ChIJjY1xqQuhmkYRHh-QHYPuuGs',
            price: priceUse + priceMonth, //12kk liikevaihto, e/kk*12 + snt/kwh*vuosikulutusennuste => 4 *12 + 0,093*2000
            quantity: 1,
            item_list_id: 'contract',
            item_list_name: 'Sähkösopimus', //Sähkösopimus tai Lisäpalvelu
          }
        ]
      }
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
};


export const purchaseEvent = async (formValues: Order, product: Variant) => {
  try {
    const { priceUse, priceMonth } = getPrices(product, formValues['consumption-estimate']);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: 'purchase',
      // SHA256 hash of the user's email address and phone. Must be 64 characters long.
      user_email: await sha256(formValues['email']),
      user_phone: await sha256(formValues['phone']),
      ecommerce: {
        transaction_id: crypto.randomUUID(),
        value: priceUse + priceMonth,
        currency: 'EUR',
        //coupon: "SUMMER_SALE",
        items: [
          {
            item_id: product.contract_template_id, // sopimuspohjan id? Tuulisähköllä esim. 21 ja 75 riippuen kestosta
            item_name: product.name, //"Tuulisähkö"
            //coupon: "xxx", //sitten kun kupongit tulee käyttöön
            index: 0,
            item_brand: 'Sähkösopimus', // Olisiko tämä parempi paikka laittaa tilausputken tyyppi: Sähkösopimus, OEK jne.
            item_variant: product.contract_period.label, //pituus tai paketin koko S M L XL
            item_category: formValues['apartment-type'], //asumismuoto appartment-type
            item_category2: formValues['preferences'], //Mitä toivoisit sähköltä
            item_category3: formValues['consumption-estimate'], //kulutusarvio "consumption-estimate"
            item_category4: formValues['apartment-size'], // asunnon koko "appartment-size"
            item_category5: formValues['customer-type'], // yritykset / kuluttajat
            location_id: 'ChIJjY1xqQuhmkYRHh-QHYPuuGs',
            price: priceUse + priceMonth, //12kk liikevaihto, e/kk*12 + snt/kwh*vuosikulutusennuste => 4 *12 + 0,093*2000
            quantity: 1,
            item_list_id: 'contract',
            item_list_name: 'Sähkösopimus', //Sähkösopimus tai Lisäpalvelu
          }
        ]
      }
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
};


export const sendPageView = (pagePath: string, pageTitle: string, data = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtualPageview',
      pageUrl: pagePath,
      pageTitle: pageTitle,
      ...data
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
}


export const additionalServicesEvent = (serviceIds: string) => {
  try {
    window._conv_q = window._conv_q || [];
    const ids = serviceIds.split(',').filter(id => id != '').map(id => parseInt(id));

    if (ids.length == 0) {
      return;
    }

    window._conv_q.push(['triggerConversion', 100478062]); // Mikä tahansa lisäpalvelu
    for (const id of ids) {
      if (id === 268) {
        window._conv_q.push(['triggerConversion', 100478063]); // Fossiilivapaa
      } else if ([248, 240, 250, 251].includes(id)) {
        window._conv_q.push(['triggerConversion', 100478064]); // Aurinkosähkö
      } else if ([264, 265, 266, 267].includes(id)) {
        window._conv_q.push(['triggerConversion', 100478065]); // Ekosähkö
      }
    }

  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
  }
}
